import { Capacitor } from '@capacitor/core';
import { api } from '@/utils/api';
import { podeGuardar } from './regrasDasContas';

/**
 * AS CONTAS LEMBRADAS NESTE NAVEGADOR — o "Trocar de conta" na web (24/10/2026; `docs/PLANO-TROCAR-DE-CONTA.md`,
 * "Navegador"). A página NÃO guarda nada: a credencial de cada conta mora num cookie HttpOnly que o servidor grava e só
 * as rotas `/api/contas-do-navegador` recebem (`backend/src/services/contasDoNavegador.ts`). Daqui só sai a lista
 * (nome, papel, usuário, se ainda vale) e os pedidos de entrar, lembrar e esquecer.
 * Só no navegador: no app, o "Trocar de conta" é o do cofre (`contasDoAparelho.ts`).
 */
export type ContaLembrada = { userId: number; nome: string; username: string; papel: string; valida: boolean };

export const noNavegador = () => !Capacitor.isNativePlatform();
export const AVISO_DO_COMPUTADOR_COMPARTILHADO = 'Não marque em computador compartilhado.';
/** O usuário a trazer preenchido no login (a conta lembrada que deixou de valer). */
export const USUARIO_DO_LOGIN = 'contas:usuario';

export async function listarLembradas(): Promise<ContaLembrada[]> {
  if (!noNavegador()) return [];
  try { return ((await api.get('/api/contas-do-navegador')).data?.contas ?? []) as ContaLembrada[]; } catch { return []; }
}

/** Lembra a conta da sessão aberta (depois do login com a caixa marcada, ou pela folha). Aluno real: nem pede. */
export async function lembrarContaAtual(u: { role?: string; username?: string } | null): Promise<void> {
  if (!noNavegador() || !podeGuardar(u)) return;
  try { await api.post('/api/contas-do-navegador/lembrar', {}); }
  catch (e) {
    const r = (e as { response?: { data?: { error?: string } } }).response;
    throw new Error(r?.data?.error ?? 'Não foi possível lembrar esta conta agora.');
  }
}

/** Entra na conta lembrada. Deixou de valer → o erro leva o usuário, para o login vir preenchido. */
export async function entrarNaLembrada(c: ContaLembrada): Promise<{ token: string; user: never }> {
  try { return (await api.post('/api/contas-do-navegador/entrar', { userId: c.userId })).data; }
  catch (e) {
    const r = (e as { response?: { status?: number; data?: { error?: string } } }).response;
    if (r?.status === 401) throw Object.assign(new Error(r.data?.error ?? 'Entre com a senha.'), { username: c.username });
    throw new Error('Sem conexão com o portal agora. Tente de novo.');
  }
}

export async function esquecerLembrada(userId: number): Promise<void> {
  if (!noNavegador()) return;
  await api.post('/api/contas-do-navegador/esquecer', { userId }).catch(() => undefined);
}

export async function sairDeTodasNoNavegador(): Promise<number> {
  return ((await api.post('/api/contas-do-navegador/sair-de-todas', {})).data?.esquecidas ?? 0) as number;
}
